import { ProgressCurveChart } from '@/pages/sites/pmis/components/ProgressCurveChart';
import type { PmisProgressCurve } from '@/pages/sites/pmis/queries';

/**
 * "○○현장 공정율 추이" 의 답을 <b>선으로</b> 보여 주는 카드 (2026-08-14 · DOC-20 §1.17).
 *
 * <h2>왜 그림이 필요한가</h2>
 * 답변 문장은 최근 몇 주만 말한다 — 여덟 줄이 넘어가면 읽히지 않기 때문이다. 그런데 추이를 묻는
 * 사람이 알고 싶은 것은 <b>모양</b>이다: 붙어 가는가, 벌어지는가, 어느 달부터 멈췄는가. 그 답은
 * 접기 전 전 구간을 봐야 나온다. 그래서 문장은 접은 값을, 카드는 원본을 든다 — 자리를 물었을 때
 * "3층입니다" 한 줄과 도면을 함께 내는 것과 같은 판단이다({@link FloorplanCard}).
 *
 * <h2>현장 화면의 그래프를 그대로 쓴다</h2>
 * 그리는 일은 {@link ProgressCurveChart} 가 이미 한다. 대화용으로 하나 더 만들면 <b>같은 숫자를
 * 두 가지 모양으로 그리는 화면</b>이 생기고, 예정 곡선의 출처·계획 일정 미입력 같은 단서가 한쪽에만
 * 붙는다. 그 단서는 지어낸 곡선을 계획으로 오해하지 않게 하는 장치라 빠지면 안 된다(DOC-33).
 *
 * <p>그래서 에이전트도 페이로드를 {@code PmisProgressCurve} 와 <b>같은 모양</b>으로 보낸다.
 * 다른 것은 점의 밀도뿐이다 — 날짜별 원본을 다 실으면 답변 하나가 수백 점을 나르므로 도구가
 * <b>주별로 접어서</b> 보낸다({@code tools/site.py:_fold_weekly}).
 */

export type ProgressCurvePayload = {
  /** 현장 이름. 어느 현장의 곡선인지는 그림만으로 알 수 없다. */
  siteName: string;
  curve: PmisProgressCurve;
};

export function ProgressCurveCard({ payload }: { payload: ProgressCurvePayload }) {
  const points = payload.curve?.points;
  // 점이 없으면 카드를 내지 않는다. 빈 그래프 틀은 "자료가 없다" 보다 나쁘다 — 0% 로 읽힌다.
  if (!points || points.length === 0) return null;

  return (
    <div className="space-y-1 max-w-[720px]">
      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
        {payload.siteName} <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>주단위 공정율</span>
      </div>
      <ProgressCurveChart curve={payload.curve} />
    </div>
  );
}
